import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// The single source of truth for premium access on the client.
// Server-side enforcement lives in the Lambdas (SUBSCRIPTION row check) —
// this store only drives UI gating and upsell surfaces.
//
// Features NOT in this list are free for everyone.
const PREMIUM_FEATURES = [
  'friend-location', // live friend positions on the map
  'run-analysis',
  'speed-charts',
  'altitude-charts',
  'export-gpx', // not yet built — reserved
  'export-csv', // not yet built — reserved
];

const useSubscriptionStore = create(
  persist(
    (set, get) => ({
      // Subscription state
      tier: 'free', // free | premium — only tiers that exist as Apple IAP products
      source: 'none', // none | iap_apple
      status: 'inactive', // inactive | active | trialing | past_due | canceled
      expiresAt: null, // ISO date string

      // Conversion tracking
      sessionsRecorded: 0,
      premiumFeaturesViewed: 0,
      lastUpgradePrompt: null,

      // Actions
      setSubscription: ({ tier, source, status, expiresAt }) => {
        set({ tier, source, status, expiresAt });
      },

      incrementSessions: () => {
        set((s) => ({ sessionsRecorded: s.sessionsRecorded + 1 }));
      },

      incrementPaywallViews: () => {
        set((s) => ({ premiumFeaturesViewed: s.premiumFeaturesViewed + 1 }));
      },

      setLastUpgradePrompt: () => {
        set({ lastUpgradePrompt: Date.now() });
      },

      // Pure — safe to call during render. `trialing` covers a StoreKit
      // introductory free trial; the local pseudo-trial was removed.
      isPremium: () => {
        const { tier, status, expiresAt } = get();
        if (tier === 'free') return false;
        if (status !== 'active' && status !== 'trialing') return false;
        return !expiresAt || new Date(expiresAt).getTime() > Date.now();
      },

      canAccessFeature: (feature) => {
        if (!PREMIUM_FEATURES.includes(feature)) return true;
        return get().isPremium();
      },

      // Reset
      reset: () => {
        set({
          tier: 'free',
          source: 'none',
          status: 'inactive',
          expiresAt: null,
        });
      },
    }),
    {
      name: 'powderlog-subscription',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export default useSubscriptionStore;
